import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Table, Modal, Form, Input, Select, Space, Popconfirm, Typography, Tag, Row, Col, DatePicker, InputNumber, App } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { projectAPI } from '../services/api';
import { formatDateTimeBeijing, formatDateBeijing } from '../utils/datetime';

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

const ProjectManagement = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await projectAPI.list();
      setProjects(response.data);
    } catch (error) {
      message.error('加载项目失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingProject(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    form.setFieldsValue(project);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await projectAPI.delete(id);
      message.success('删除成功');
      loadProjects();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const submitData = {
        ...values,
        planned_start_date: values.planned_start_date ? values.planned_start_date.format('YYYY-MM-DD') : null,
        planned_end_date: values.planned_end_date ? values.planned_end_date.format('YYYY-MM-DD') : null,
      };
      
      if (editingProject) {
        await projectAPI.update(editingProject.id, submitData);
        message.success('更新成功');
      } else {
        await projectAPI.create(submitData);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadProjects();
    } catch (error) {
      message.error(error.response?.data?.error || '操作失败');
    }
  };

  const columns = [
    {
      title: '项目名称',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
    },
    {
      title: '项目类型',
      dataIndex: 'type',
      key: 'type',
      width: '15%',
      render: (type) => {
        const colors = {
          '公路': 'blue',
          '铁路': 'green',
          '水利': 'cyan',
          '建筑': 'orange',
          '矿山': 'purple',
          '其他': 'default',
        };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      },
    },
    {
      title: '项目分类',
      dataIndex: 'category',
      key: 'category',
      width: '15%',
      render: (category) => category ? <Tag>{category}</Tag> : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: '12%',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status === 'active' ? '进行中' : '已归档'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '18%',
      render: (date) => formatDateTimeBeijing(date),
    },
    {
      title: '操作',
      key: 'action',
      width: '15%',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除"
            description="确定要删除这个项目吗？"
            onConfirm={(e) => {
              e.stopPropagation();
              handleDelete(record.id);
            }}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginBottom: '8px' }}>项目管理</Title>
        <Text type="secondary">个人项目资料管理</Text>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={projects}
          loading={loading}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => navigate(`/projects/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个项目`,
          }}
        />
      </Card>

      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleAdd}
        style={{
          position: 'fixed',
          right: '24px',
          bottom: '24px',
          boxShadow: '0 4px 12px rgba(24, 144, 255, 0.4)',
          zIndex: 1000,
          height: '56px',
          fontSize: '16px',
        }}
      >
        新建项目
      </Button>

      <Modal
        title={editingProject ? '编辑项目' : '新建项目'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Title level={5}>基本信息</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="项目名称"
                rules={[{ required: true, message: '请输入项目名称' }]}
              >
                <Input placeholder="请输入项目名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="项目类型"
                rules={[{ required: true, message: '请选择项目类型' }]}
              >
                <Select placeholder="请选择项目类型">
                  <Option value="公路">公路工程</Option>
                  <Option value="铁路">铁路工程</Option>
                  <Option value="水利">水利工程</Option>
                  <Option value="建筑">建筑工程</Option>
                  <Option value="矿山">矿山工程</Option>
                  <Option value="其他">其他</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category"
                label="项目分类"
              >
                <Select placeholder="请选择项目分类" allowClear>
                  <Option value="新建">新建项目</Option>
                  <Option value="改建">改建项目</Option>
                  <Option value="扩建">扩建项目</Option>
                  <Option value="技改">技术改造</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="province"
                label="所在省份"
              >
                <Select placeholder="请选择省份" allowClear>
                  <Option value="北京">北京</Option>
                  <Option value="天津">天津</Option>
                  <Option value="河北">河北</Option>
                  <Option value="山西">山西</Option>
                  <Option value="内蒙古">内蒙古</Option>
                  <Option value="辽宁">辽宁</Option>
                  <Option value="吉林">吉林</Option>
                  <Option value="黑龙江">黑龙江</Option>
                  <Option value="上海">上海</Option>
                  <Option value="江苏">江苏</Option>
                  <Option value="浙江">浙江</Option>
                  <Option value="安徽">安徽</Option>
                  <Option value="福建">福建</Option>
                  <Option value="江西">江西</Option>
                  <Option value="山东">山东</Option>
                  <Option value="河南">河南</Option>
                  <Option value="湖北">湖北</Option>
                  <Option value="湖南">湖南</Option>
                  <Option value="广东">广东</Option>
                  <Option value="广西">广西</Option>
                  <Option value="海南">海南</Option>
                  <Option value="重庆">重庆</Option>
                  <Option value="四川">四川</Option>
                  <Option value="贵州">贵州</Option>
                  <Option value="云南">云南</Option>
                  <Option value="西藏">西藏</Option>
                  <Option value="陕西">陕西</Option>
                  <Option value="甘肃">甘肃</Option>
                  <Option value="青海">青海</Option>
                  <Option value="宁夏">宁夏</Option>
                  <Option value="新疆">新疆</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="location"
            label="具体位置"
          >
            <Input placeholder="请输入具体位置" />
          </Form.Item>

          <Form.Item
            name="description"
            label="项目描述"
          >
            <TextArea
              placeholder="请输入项目描述"
              rows={3}
            />
          </Form.Item>

          <Title level={5}>项目规模</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="total_investment"
                label="项目总投资（万元）"
              >
                <InputNumber placeholder="请输入总投资" style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="construction_period"
                label="建设工期（月）"
              >
                <InputNumber placeholder="请输入建设工期" style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="land_area"
                label="占地面积（平方米）"
              >
                <InputNumber placeholder="请输入占地面积" style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="earthwork_volume"
                label="土石方总量（立方米）"
              >
                <InputNumber placeholder="请输入土石方总量" style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="construction_scale"
            label="建设规模"
          >
            <TextArea
              placeholder="请输入建设规模"
              rows={2}
            />
          </Form.Item>

          <Title level={5}>项目时间</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="planned_start_date"
                label="计划开工时间"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="planned_end_date"
                label="计划完工时间"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Title level={5}>项目单位</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="construction_unit"
                label="建设单位"
              >
                <Input placeholder="请输入建设单位" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="design_unit"
                label="设计单位"
              >
                <Input placeholder="请输入设计单位" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="construction_contractor"
                label="施工单位"
              >
                <Input placeholder="请输入施工单位" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="supervision_unit"
                label="监理单位"
              >
                <Input placeholder="请输入监理单位" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default ProjectManagement;
